// ---- sounds (WebAudio, no assets) and the family's voice clips ----
// The context, the four chimes a tap earns, the clip store and the rule about
// who may speak when. Every game in this house speaks the same way: a letter is
// named on the way into its slot, a word is said twice with a breath between,
// and the grown-up's recorded lines never talk over each other.
//
// What the games do NOT share is what there is to say, so the roster reaches
// this module through setVoice() rather than by importing a game's data: the
// letters and their spoken names, the words and their vowelled forms (both only
// for the TTS fallback), and `want` — the clips to fetch, IN ORDER, because the
// first round's letters wait on the grown-up's hello and a manifest that asks
// for it late makes the game open in silence.
import {asset,got} from './assets';
import {scriptOf} from './letters';
import {pick} from './util';

export interface VoiceCfg{
  say:Record<string,string>;      // letter -> its spoken name, for the TTS fallback
  dia:Record<string,string>;      // word -> its vowelled form, for the TTS fallback
  // Every clip to fetch, in the order they matter. A third element is the
  // folder that one clip is borrowed from, by path, when it is not the game's
  // own: the tracing board (trace.ts) says a syllable no word of the game is
  // spelled with, and vowels/ already has every one of them.
  want:[string,string,string?][];
  dir?:string;                    // where they live; 'audio/' beside the page by default
}
let cfg:VoiceCfg={say:{},dia:{},want:[]};
export const setVoice=(c:VoiceCfg)=>{cfg=c;};


let AC:AudioContext|null=null;
// A decoded clip, 'missing' once its fetch has failed, undefined while it is
// still on its way. Keyed by prefix and name — L a letter, W a word, S a thing's
// own noise, R one of the grown-up's recorded lines — matching the files in
// the game's audio/ (see clipUrl).
export const clips:Record<string,AudioBuffer|'missing'|undefined>={};
let clipsLoaded=false, audioOK=false;
// What is sounding now, so that it can be cut: his voice (a letter, a name, a
// line of his) and the animals' cries, kept apart because his line over a cry
// is the reaction and stays, while a tap silences the lot.
let liveVoice:AudioBufferSourceNode[]=[], liveCries:AudioBufferSourceNode[]=[];
// Everything said on a delay carries the generation it was queued in; a tap in
// between moves the generation on (interrupt), and the stale line stays unsaid.
let sayGen=0;
export const interrupt=()=>{sayGen++;};
export function after(ms:number,fn:()=>void){const g=sayGen;setTimeout(()=>{if(g===sayGen)fn();},ms);}   // unless something else speaks first
export const speechGen=()=>sayGen;
// When he will have finished whatever he is saying, in performance.now() ms.
// Every line moves it on, as it is queued (reserve) and as it plays (playClip);
// a tap (stopAll) brings it back to now, because whatever was queued for him
// is void once the child has cut in. He only ever says one thing at a time: a
// new line stops the last, the round waits for the letter before the animal
// arrives (finishWord, round.ts), and the field holds its cries until he is
// done (hushed, jiddo.ts).
let voiceUntil=-1e9;
export const reserve=(ms:number)=>{voiceUntil=Math.max(voiceUntil,performance.now()+ms);};
export const speaking=(t:number)=>t<voiceUntil;
export const voiceEnd=()=>voiceUntil;
// When his mouth is moving: a name or a line of his, from the moment it sounds
// (not from when it was queued — a line queued behind a delay may never play)
// until it ends. A letter is not talking: a tap names one, and a gesture that
// started on every tap would never settle. This is what his talk clip plays
// under (jiddo.ts); `speaking` above is the hush, which is wider.
let talkUntil=-1e9;
export const talking=(t:number)=>t<talkUntil;
function ac(){
  if(!AC)AC=new (window.AudioContext||(window as unknown as {webkitAudioContext:typeof AudioContext}).webkitAudioContext)();
  if(AC.state==='suspended')AC.resume();
  return AC;
}
// The first touch is what the browser lets audio start on: the context is made
// and the clips asked for there, and the round announces itself once it has.
export function unlockAudio(){try{ac();loadClips();audioOK=true;}catch(err){}}
export const audioReady=()=>audioOK;
// One note, swept from f0 to f1 and faded out. Every written noise in the house
// is a handful of these, which is why it is exported: a game's own creatures
// build their voices from it (jiddo/src/cries.ts) without reaching for the
// context themselves.
export function beep(f0:number,f1:number,dur:number,type:OscillatorType,vol:number,delay?:number){try{
  const c=ac(),t0=c.currentTime+(delay||0),o=c.createOscillator(),g=c.createGain();
  o.type=type;o.frequency.setValueAtTime(f0,t0);o.frequency.exponentialRampToValueAtTime(Math.max(1,f1),t0+dur);
  g.gain.setValueAtTime(vol,t0);g.gain.exponentialRampToValueAtTime(0.001,t0+dur);
  o.connect(g);g.connect(c.destination);o.start(t0);o.stop(t0+dur+0.05);
}catch(err){}}
export const sndPop  =(i?:number)=>beep(480+(i||0)*45,880+(i||0)*45,.09,'sine',.15);
export const sndNudge=()=>beep(330,300,.10,'sine',.09);            // "not that one" — never a buzzer
export const sndPoof =()=>{beep(180,900,.22,'sine',.18);beep(900,240,.18,'triangle',.10,.05);};
export const sndYay  =()=>[523,659,784,1047].forEach((f,i)=>beep(f,f,.16,'triangle',.16,i*.11));
export const sndReact=(n:number)=>{if(n<=5)beep(392,523,.16,'triangle',.13);else{beep(523,784,.15,'triangle',.15);beep(784,988,.13,'triangle',.12,.13);}};
// audio/<prefix>_<key>.mp3 beside the page: the one layout every game's clips share
const clipUrl=(p:string,k:string,dir?:string)=>(dir??cfg.dir??'audio/')+p+'_'+encodeURIComponent(k)+'.mp3';
export function loadClips(){
  if(clipsLoaded)return; clipsLoaded=true;
  const want=cfg.want;                          // built by the game, hello first (see setVoice)
  for(const [p,k,dir] of want)
    fetch(asset(clipUrl(p,k,dir))).then(r=>{if(!got(r))throw 0;return r.arrayBuffer()})
      .then(b=>new Promise<AudioBuffer>((res,rej)=>ac().decodeAudioData(b,res,rej)))
      .then(buf=>{clips[p+k]=buf;}).catch(()=>{clips[p+k]='missing';});
}
export const has=(k:string)=>!!clips[k]&&clips[k]!=='missing';
// the length of a clip that is here, in ms; 0 for one that is not
export const clipMs=(k:string)=>{const b=clips[k];return b&&b!=='missing'?b.duration*1000:0;};
// A moment is three slots and rarely three takes: never draw a silent one.
export const pickClip=(l:string[])=>{const got=l.filter(c=>has('R'+c)); return pick(got.length?got:l);};
const cut=(l:AudioBufferSourceNode[])=>{for(const s of l){try{s.stop()}catch(err){}}l.length=0;};
function stopVoice(){cut(liveVoice);talkUntil=performance.now();}                                  // his line stops; a cry under it carries on
function stopAll(){cut(liveVoice);cut(liveCries);voiceUntil=talkUntil=performance.now();}   // a tap: everything stops, and the letter is heard alone
// true when it played, false when it is known to be missing, 'pending' while it is still loading
export function playClip(p:string,k:string,delay?:number,vol?:number):boolean|'pending'{
  const buf=clips[p+k];
  if(!buf||buf==='missing')return buf?false:'pending';
  const c=ac(),s=c.createBufferSource(),live=p==='S'?liveCries:liveVoice;
  s.buffer=buf;
  if(vol===undefined)s.connect(c.destination);
  else{const g=c.createGain();g.gain.value=vol;s.connect(g);g.connect(c.destination);}
  s.start(c.currentTime+(delay||0));
  s.onended=()=>{const i=live.indexOf(s);if(i>=0)live.splice(i,1);};
  live.push(s);
  if(live===liveVoice)reserve(((delay||0)+buf.duration)*1000);
  if(p==='R'||p==='W')talkUntil=Math.max(talkUntil,performance.now()+((delay||0)+buf.duration)*1000);
  return true;
}
// the robot voice, `times` over; a guess at its length keeps the field quiet for about as long
function utter(txt:string,times=1){try{
  if(!('speechSynthesis' in window))return;
  for(let i=0;i<times;i++){const u=new SpeechSynthesisUtterance(txt);u.lang=scriptOf().lang;u.rate=.8;speechSynthesis.speak(u);}
  reserve(times*(500+90*txt.length));
}catch(err){}}
// TTS only when a clip is confirmed missing — a clip still downloading is not a reason to use a robot
export function sayLetter(ch:string){stopAll();if(playClip('L',ch)!==true&&clips['L'+ch]==='missing')utter(cfg.say[ch]||ch);}
const BREATH=0.35;                              // between the two sayings of a name, in seconds
// twice, with a breath between; true once it is sounding, false while the clip is still on its way.
// `once` is the tracing board's: the name read back over a word the child has
// just watched come together, where twice is a question being asked again.
export function sayWord(w:string,once=false){
  stopAll(); const buf=clips['W'+w];
  if(buf&&buf!=='missing'&&playClip('W',w)===true){if(!once)playClip('W',w,buf.duration+BREATH);return true;}
  if(buf==='missing'){utter(cfg.dia[w]||w,once?1:2);return true;}
  return false;
}
export const wordMs=(w:string)=>2*clipMs('W'+w)+BREATH*1000;   // how long sayWord takes
// Recorded only. The field goes quiet for it as it is queued, so no cry starts
// in the delay; when it plays it stops whatever line of his was still going.
export function sayPhrase(line:string,delay?:number){
  reserve((delay||0)+clipMs('R'+line));
  after(delay||0,()=>{stopVoice();playClip('R',line);});
}